import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { INSTALL_DISMISS_KEY } from "@/lib/listenApi";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

// Subtle, dismissible Add-to-Home-Screen hint. Never interrupts playback.
export function InstallHint() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(INSTALL_DISMISS_KEY) === "1" || isStandalone()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isIOS()) {
      setIos(true);
      const t = setTimeout(() => setShow(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => { localStorage.setItem(INSTALL_DISMISS_KEY, "1"); setShow(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  if (!show) return null;
  return (
    <div
      data-testid="listen-install-hint"
      className="mx-4 mb-4 flex items-center gap-3 rounded-2xl border border-teal-500/20 bg-[#0d1219]/90 px-4 py-3 backdrop-blur animate-[fadeUp_0.5s_ease]"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-500/10">
        {ios ? <Share className="h-4 w-4 text-teal-300" /> : <Download className="h-4 w-4 text-teal-300" />}
      </div>
      <div className="min-w-0 flex-1 text-[13px] leading-snug text-slate-300">
        {ios ? (
          <>Add to your Home Screen — tap <span className="text-teal-200">Share</span> then{" "}
          <span className="text-teal-200">Add to Home Screen</span>.</>
        ) : (
          <>Add Beta Listener to your Home Screen for one-tap access.</>
        )}
      </div>
      {!ios && (
        <button
          data-testid="listen-install-accept"
          onClick={install}
          className="shrink-0 rounded-full bg-teal-400 px-3.5 py-1.5 text-xs font-semibold text-slate-950 hover:bg-teal-300"
        >
          Add
        </button>
      )}
      <button data-testid="listen-install-dismiss" onClick={dismiss} className="shrink-0 text-slate-500 hover:text-slate-300">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
